import { memberBeneficiaryQuery, myLocationQueryOptions, payoutDetailsForMemberQuery } from "@/api/me";
import { queryClient } from "@/api/utils";
import { currencyForCountry } from "@common/utils/claims";
import { toDateOnly } from "@common/utils/date";
import { CreateClaimRequest, CreateClaimResponse } from "@givenwell/marketplace-api";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { fallback, object, optional, string } from "valibot";
import { FormStep } from "./FormStep";
import { SuccessStep } from "./SuccessStep";

export const Route = createFileRoute("/_auth/_app/claims/new")({
  component: NewClaim,
  validateSearch: object({
    eap_supporter_id: fallback(optional(string()), undefined),
  }),
  loader: async () => {
    const [settings, location] = await Promise.all([
      queryClient.ensureQueryData(payoutDetailsForMemberQuery),
      queryClient.ensureQueryData(myLocationQueryOptions),
    ]);

    // Prefer the member's bank-account currency when they've set one up — it's the strongest
    // signal of what they'll be reimbursed in. On a first claim (no bank account yet) fall back
    // to their location's local currency: members in EAP-only markets (e.g. PH) might have an
    // NZD-based wallet/token currency but pay for, and claim, things locally, so the wallet
    // currency (settings.currency_code) is only the last resort.
    let bankCurrency: string | undefined;
    if (settings.beneficiary_id) {
      const beneficiary = await queryClient.ensureQueryData(memberBeneficiaryQuery);
      bankCurrency = beneficiary.currency;
    }

    const resolved = bankCurrency ?? currencyForCountry(location?.country_code) ?? settings.currency_code ?? "nzd";
    // Server currency casing isn't always consistent, and doc_currency is lowercase.
    const defaultCurrency = resolved.toLowerCase() as CreateClaimRequest["doc_currency"];
    return { defaultCurrency };
  },
});

function NewClaim() {
  const { eap_supporter_id: eapSupporterId } = Route.useSearch();
  const { defaultCurrency } = Route.useLoaderData();
  const [request, setRequest] = useState<CreateClaimRequest>({
    vendor_name: "",
    doc_date: toDateOnly(new Date()),
    doc_amount_cents: -1,
    doc_currency: defaultCurrency,
    category_id: null,
    eap_supporter_id: eapSupporterId,
    attachment: {
      file_name: "",
      mime_type: "",
      note: null,
      size_bytes: 0,
      uploaded_file_path: "",
    },
  });
  const [response, setResponse] = useState<CreateClaimResponse>({
    claim_id: "clm_1000",
    friendly_id: "1000",
  });
  const [step, setStep] = useState<"form" | "success">("form");
  const navigate = useNavigate();

  if (step === "form") {
    return (
      <FormStep
        request={request}
        onRequestChange={setRequest}
        onBack={() => {
          navigate({
            to: "/claims",
            replace: true,
          });
        }}
        onSubmit={res => {
          setStep("success");
          setResponse(res);
        }}
      />
    );
  }
  return (
    <SuccessStep
      request={request}
      response={response}
      onSubmit={() => {
        navigate({
          to: "/claims",
          replace: true,
        });
      }}
    />
  );
}
